import { useState } from "react"
import type { UseFormReturn } from "react-hook-form"

import { usePaymentTermsContext } from "../contexts/PaymentTermsContext"

import useCompanyAccountingTermsActions from "@/screens/CompanySettings/Accounting/hooks/useCompanyAccountingTermsActions"
import type { PaymentTermsFormValues } from "@/screens/CompanySettings/Accounting/schemas/PaymentTermsSchema"
import { serializeUpsertInput } from "@/screens/CompanySettings/Accounting/utils/common"

const usePaymentTermsDialog = ({
  onSuccess,
  onClose,
  methods,
}: {
  onSuccess: () => void
  onClose: () => void
  methods: UseFormReturn<PaymentTermsFormValues>
}): {
  setIsCancelConfirmationOpen: (isOpen: boolean) => void
  isCancelConfirmationOpen: boolean
  handleSubmit: () => Promise<void>
  canSubmit: boolean
  handleCancel: () => void
} => {
  const {
    handleSubmit: zodHandleSubmit,
    formState: { isValid, isDirty, errors, isSubmitting },
  } = methods
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const { isCompanyVendor, isCompanyCustomer } = usePaymentTermsContext()

  const { addAccountingTermData } = useCompanyAccountingTermsActions({
    onSuccess,
  })

  const handleSubmit = zodHandleSubmit(async (input) => {
    const inputs = serializeUpsertInput(input, isCompanyVendor, isCompanyCustomer)
    await addAccountingTermData(inputs)
  })

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  // Ensure the form is valid, dirty, and has no errors (image upload restrictions) before allowing submission
  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isSubmitting

  return {
    setIsCancelConfirmationOpen,
    isCancelConfirmationOpen,
    canSubmit,
    handleSubmit,
    handleCancel,
  }
}

export default usePaymentTermsDialog
